import { usePlatform } from "@portal/hooks/usePlatform";
import { useSearchAnalytics } from "@portal/hooks/useSearchAnalytics";
import { useGlobalSearchDispatch } from "@portal/providers/GlobalSearchProvider";
import { useCallback, useMemo, useRef, useState } from "react";
import { Modal } from "../../layout/Modal";
import { cn } from "../../lib/utils";
import { ClickableIcon } from "../../primitives";
import { Checkbox } from "../Checkbox";
import { ErrorDisplay } from "../ErrorDisplay";
import { KeyboardShortcut } from "../KeyboardShortcut";
import { SearchInput } from "../SearchInput";
import { Spinner } from "../Spinner";
import { GlobalSearchFilterTabs } from "./GlobalSearchFilterTabs";
import { GlobalSearchResult } from "./GlobalSearchResult";
import { GlobalSearchResultsCategory, getCategoryTitle } from "./GlobalSearchResultsCategory";
import { type ActiveFilter, ActiveFilterEnum } from "./types";
import { useGlobalSearchData } from "./useGlobalSearchData";
import { useStickyScrollBehavior, type VisibleCategory } from "./useStickyScrollBehavior";

// Header height used for calculating offsets in sticky scroll behavior
// Getting this dynamically is possible but adds complexity and causes the implementation to be more brittle,
// so using a constant based on the current design. Should be updated if the design changes significantly.
const HEADER_HEIGHT = 32;

export const GlobalSearchModal = () => {
	const [globalSearchTerm, setGlobalSearchTerm] = useState("");
	const [showClosed, setShowClosed] = useState(false);
	const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
	const { closeGlobalSearch } = useGlobalSearchDispatch();
	const { onSearchTermChange, onResultClicked } = useSearchAnalytics("modal");
	const { isMac } = usePlatform();

	const {
		results: { recentlyViewed, accounts, bonds, bondRequests },
		isLoading,
		error,
		refetchPreviews,
	} = useGlobalSearchData({
		searchTerm: globalSearchTerm,
		activeFilter,
		showClosed,
	});

	const handleSearchTermChange = (value: string) => {
		setGlobalSearchTerm(value);
		onSearchTermChange(value);
	};

	const handleFilterChange = useCallback(
		(filter: ActiveFilter) => {
			// No state update if the same filter is clicked
			if (filter === activeFilter) {
				return;
			}

			setActiveFilter(filter);
		},
		[activeFilter, setActiveFilter],
	);

	const isAllWithSearch = activeFilter === ActiveFilterEnum.ALL && globalSearchTerm !== "";

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const pinnedContainerRef = useRef<HTMLDivElement>(null);

	const visibleCategories = useMemo<VisibleCategory[]>(() => {
		if (!isAllWithSearch) return [];
		const categories: VisibleCategory[] = [];
		if (accounts && accounts.length > 0) {
			categories.push({
				category: ActiveFilterEnum.ACCOUNTS,
				resultCount: accounts.length,
			});
		}
		if (bondRequests && bondRequests.length > 0) {
			categories.push({
				category: ActiveFilterEnum.REQUESTS,
				resultCount: bondRequests.length,
			});
		}
		if (bonds && bonds.length > 0) {
			categories.push({
				category: ActiveFilterEnum.BONDS,
				resultCount: bonds.length,
			});
		}
		return categories;
	}, [isAllWithSearch, accounts, bondRequests, bonds]);

	const stickyTopOffsets = useMemo(() => {
		const offsets = new Map<ActiveFilterEnum, number>();
		visibleCategories.forEach((vc, index) => {
			offsets.set(vc.category, index * HEADER_HEIGHT);
		});
		return offsets;
	}, [visibleCategories]);

	useStickyScrollBehavior(scrollContainerRef, pinnedContainerRef, visibleCategories);

	const handleJumpTo = useCallback(
		(category: ActiveFilterEnum) => {
			const container = scrollContainerRef.current;
			if (!container) return;
			const sentinel = container.querySelector<HTMLElement>(
				`[data-category-watcher="${category}"]`,
			);
			if (!sentinel) return;
			const offset = stickyTopOffsets.get(category) ?? 0;
			const target = sentinel.offsetTop - offset;
			container.scrollTop = target;
			container.focus({ preventScroll: true });
			requestAnimationFrame(() => {
				if (Math.abs(container.scrollTop - target) > 1) {
					container.scrollTop = target;
				}
			});
		},
		[stickyTopOffsets],
	);

	return (
		<>
			<Modal onClickOutside={closeGlobalSearch} autoFocus>
				<div
					data-testid="global-search-modal"
					className="fixed inset-0 bg-black/30 z-[200] flex items-start justify-center pt-[10vh]"
					onClick={closeGlobalSearch}
				>
					<div
						className="bg-white rounded-[8px] shadow-2xl w-9/12 min-w-[500px] max-w-[850px] flex flex-col"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div
							data-testid="global-search-modal-header"
							className="flex flex-col border-b border-bc-default p-4"
						>
							<div className="flex-1 flex items-center gap-3 mb-3">
								<div className="flex-1 flex items-center gap-3 pr-2 rounded-md outline outline-1 outline-offset-[-1px] outline-bc-strong focus-within:outline-bc-stronger focus-within:hover:outline-2">
									<SearchInput
										testId="global-search-input"
										defaultValue={globalSearchTerm}
										placeholder="Search..."
										onChange={handleSearchTermChange}
										className="flex-1"
										inputClassName="flex-1 text-[15px] text-primary placeholder:text-tertiary"
										hideFocusStyles
										autofocus
									/>
									<div
										data-testid="global-search-shortcut-display"
										className="flex items-center gap-2 text-xs text-tertiary bg-surface-neutral px-2 py-1 rounded"
									>
										{`${isMac ? "⌘" : "Ctrl+"}K`}
									</div>
								</div>
								<ClickableIcon
									data-testid="close-modal-button"
									type="x-mark"
									color="tertiary"
									size="small"
									onClick={closeGlobalSearch}
								/>
							</div>

							{/* Filter Buttons */}
							<div className="flex flex-1 items-center justify-between">
								<GlobalSearchFilterTabs activeFilter={activeFilter} onChange={handleFilterChange} />

								<div
									data-testid="show-closed-checkbox"
									className="flex flex-1 items-center justify-end"
								>
									<Checkbox checked={showClosed} onChange={(checked) => setShowClosed(checked)} />
									<span className="text-sm ml-2">Show closed</span>
								</div>
							</div>
						</div>

						{/* Loading / Error States */}
						{isLoading || (error && (!accounts || !bondRequests || !bonds)) ? (
							<div className="flex items-center justify-center w-full h-full my-12">
								{isLoading ? (
									<div data-testid="global-search-loading">
										<Spinner />
									</div>
								) : (
									<ErrorDisplay
										error={error}
										componentToDisplay={
											<div
												data-testid="global-search-error"
												className="flex w-full p-4 text-sm justify-center items-center"
											>
												<span>Unable to load previews.</span>
												<button
													data-testid="retry-link"
													className="cursor-pointer m-1 text-link hover:text-link-hover"
													onClick={() => refetchPreviews()}
												>
													Retry
												</button>
											</div>
										}
									/>
								)}
							</div>
						) : (
							<div className="flex flex-col items-start justify-center w-full">
								{/* Recently Viewed */}
								{activeFilter === ActiveFilterEnum.ALL && !globalSearchTerm && (
									<>
										<div className="text-xs text-secondary font-medium p-2">Recently viewed</div>
										<div className="flex items-center justify-center w-full mt-2 pb-4">
											{!recentlyViewed || recentlyViewed.length === 0 ? (
												<div className="text-sm text-tertiary">No recently viewed</div>
											) : (
												<div className="flex flex-col w-full overflow-y-auto text-xs">
													{recentlyViewed?.map((result, index) => (
														<GlobalSearchResult
															key={`${index}-${result.relationType}-${result.entity.id}`}
															{...result}
															resultSource="recentlyViewed"
															onResultClicked={onResultClicked}
														/>
													))}
												</div>
											)}
										</div>
									</>
								)}
								<div className="relative w-full">
									<div
										ref={scrollContainerRef}
										tabIndex={-1}
										className={cn(
											"w-full outline-none",
											activeFilter === ActiveFilterEnum.ALL &&
												"max-h-[450px] overflow-y-auto scrollbar-gutter-stable scrollbar-med",
										)}
									>
										{/* Accounts */}
										{((isAllWithSearch && accounts!.length > 0) ||
											activeFilter === ActiveFilterEnum.ACCOUNTS) && (
											<GlobalSearchResultsCategory
												category={ActiveFilterEnum.ACCOUNTS}
												results={accounts}
												searchTerm={globalSearchTerm === "" ? undefined : globalSearchTerm}
												stickyTopOffset={stickyTopOffsets.get(ActiveFilterEnum.ACCOUNTS)}
												scrollContainerRef={scrollContainerRef}
												onJumpTo={() => handleJumpTo(ActiveFilterEnum.ACCOUNTS)}
												onResultClicked={onResultClicked}
											/>
										)}
										{/* Bond Requests */}
										{((isAllWithSearch && bondRequests!.length > 0) ||
											activeFilter === ActiveFilterEnum.REQUESTS) && (
											<GlobalSearchResultsCategory
												category={ActiveFilterEnum.REQUESTS}
												results={bondRequests}
												searchTerm={globalSearchTerm === "" ? undefined : globalSearchTerm}
												stickyTopOffset={stickyTopOffsets.get(ActiveFilterEnum.REQUESTS)}
												scrollContainerRef={scrollContainerRef}
												onJumpTo={() => handleJumpTo(ActiveFilterEnum.REQUESTS)}
												onResultClicked={onResultClicked}
											/>
										)}
										{/* Bonds */}
										{((isAllWithSearch && bonds!.length > 0) ||
											activeFilter === ActiveFilterEnum.BONDS) && (
											<GlobalSearchResultsCategory
												category={ActiveFilterEnum.BONDS}
												results={bonds}
												searchTerm={globalSearchTerm === "" ? undefined : globalSearchTerm}
												stickyTopOffset={stickyTopOffsets.get(ActiveFilterEnum.BONDS)}
												scrollContainerRef={scrollContainerRef}
												onJumpTo={() => handleJumpTo(ActiveFilterEnum.BONDS)}
												onResultClicked={onResultClicked}
											/>
										)}
										{isAllWithSearch &&
											accounts!.length === 0 &&
											bondRequests!.length === 0 &&
											bonds!.length === 0 && (
												<div className="flex-1 w-full gap-2 text-xs text-tertiary text-center px-4 pb-4 overflow-y-auto max-h-[410px] mt-4">
													<span data-testid="global-search-no-results">
														No results for "{globalSearchTerm}"
													</span>
												</div>
											)}
									</div>
									{/* Pinned bottom headers — outside scroll container, visibility managed by hook via DOM */}
									<div
										ref={pinnedContainerRef}
										className="absolute bottom-0 left-0 right-0 z-20 flex flex-col w-full rounded-b-[8px] overflow-hidden"
										style={{ display: "none" }}
									>
										{visibleCategories.map((vc) => (
											<button
												key={vc.category}
												data-pinned-category={vc.category}
												data-hidden=""
												type="button"
												className="flex items-center gap-1 text-xs text-secondary font-medium p-2 pr-[15px] w-full bg-surface-neutral border-t border-bc-subtle cursor-pointer hover:bg-surface-light data-[hidden]:hidden"
												onClick={(e) => {
													e.currentTarget.blur();
													handleJumpTo(vc.category);
												}}
											>
												<div>{getCategoryTitle(vc.category)}</div>
												<div>
													{`(${vc.resultCount} ${vc.resultCount === 1 ? "RESULT" : "RESULTS"})`}
												</div>
												<div className="ml-auto text-link hover:text-link-hover">Jump to</div>
											</button>
										))}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</Modal>
			<KeyboardShortcut shortcut={{ key: "Escape" }} onShortcutPress={closeGlobalSearch} />
		</>
	);
};
